import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Logo from '../assets/images/logo.svg';
import { AGB_URL, PRIVACY_URL } from '../constants/links';
import ReactMarkdown from 'react-markdown';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const API_BASE_URL = 'https://r2cok49t3g.execute-api.eu-central-1.amazonaws.com/prod';
const API_KEY = 'xVub93q3wq3dyZ0TgqyC24FwsK53UVbM7FOv171v';

const formatErrorMessage = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if (value.message) return String(value.message);
    if (value.error) return String(value.error);
    try {
      return JSON.stringify(value);
    } catch (err) {
      return 'Unbekannter Fehler';
    }
  }
  return String(value);
};

const isOptInCompleted = (spec) => {
  if (!spec || typeof spec !== 'object') return false;

  const booleanFlags = [
    'already_completed',
    'already_submitted',
    'optin_completed',
    'is_completed',
    'completed',
    'is_complete',
    'already_opted_in'
  ];

  for (const key of booleanFlags) {
    if (spec[key] === true) return true;
  }

  const statusValue = spec.status || spec.optin_status || spec.state;
  if (typeof statusValue === 'string') {
    const normalized = statusValue.toLowerCase();
    if (['completed', 'complete', 'submitted', 'done', 'already_completed', 'already_submitted', 'opted_in'].includes(normalized)) {
      return true;
    }
  }

  return false;
};

export default function OptIn() {
  const query = useQuery();
  const auditId = query.get('audit_id');
  const accessKey = query.get('access_key');
  const optinId = query.get('optin_id');

  const [loading, setLoading] = useState(true);
  const [optinSpec, setOptinSpec] = useState(null);
  const [error, setError] = useState(null);
  const [checkedBoxIds, setCheckedBoxIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [isAlreadyCompleted, setIsAlreadyCompleted] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [requiresScroll, setRequiresScroll] = useState(false);
  const contentRef = useRef(null);
  const actionsRef = useRef(null);

  const optinBody = useMemo(() => {
    if (typeof optinSpec?.body === 'string' && optinSpec.body.trim().length > 0) {
      return optinSpec.body;
    }
    return 'Bitte bestätigen Sie die folgende Zustimmung.';
  }, [optinSpec]);

  useEffect(() => {
    let isMounted = true;

    const fetchOptInSpec = async () => {
      if (!auditId || !optinId) {
        setError('Fehlende Parameter: audit_id oder optin_id.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setIsAlreadyCompleted(false);
        setSubmitSuccess(null);
        setSubmitError(null);
        setCheckedBoxIds([]);
        setHasScrolledToBottom(false);

        const url = `${API_BASE_URL}/audits/optin-definition/${encodeURIComponent(auditId)}/${encodeURIComponent(optinId)}${accessKey ? `?access_key=${encodeURIComponent(accessKey)}` : ''}`;
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${API_KEY}`,
            'Content-Type': 'application/json'
          }
        });

        if (!isMounted) return;

        setOptinSpec(response.data || null);
        setIsAlreadyCompleted(isOptInCompleted(response.data));
      } catch (err) {
        if (!isMounted) return;
        const errorMessage = formatErrorMessage(err?.response?.data) || err?.message || 'Fehler beim Laden der Opt-In Daten';
        setError(errorMessage);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchOptInSpec();

    return () => {
      isMounted = false;
    };
  }, [auditId, optinId, accessKey]);

  useEffect(() => {
    setHasScrolledToBottom(false);
  }, [optinBody]);

  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;

    const checkOverflow = () => {
      const overflow = node.scrollHeight > node.clientHeight + 1;
      setRequiresScroll(overflow);
      if (!overflow) {
        setHasScrolledToBottom(true);
      }
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(node);

    return () => {
      resizeObserver.disconnect();
    };
  }, [optinBody]);

  const handleCheckboxToggle = (checkboxId) => {
    setCheckedBoxIds((prev) => {
      if (prev.includes(checkboxId)) {
        return prev.filter((id) => id !== checkboxId);
      }
      return [...prev, checkboxId];
    });
  };

  const getMissingRequiredCheckboxes = () => {
    if (!optinSpec?.checkboxes) return [];
    return optinSpec.checkboxes
      .filter((checkbox) => checkbox.optional === false)
      .filter((checkbox) => !checkedBoxIds.includes(checkbox.id));
  };

  const handleSubmit = async () => {
    if (!auditId || !optinId) {
      setSubmitError('Fehlende Parameter: audit_id oder optin_id.');
      return;
    }

    const missingRequired = getMissingRequiredCheckboxes();
    if (missingRequired.length > 0) {
      setSubmitError('Bitte bestätigen Sie alle erforderlichen Checkboxen.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const payload = {
        optin_id: optinId,
        checked_box_ids: checkedBoxIds
      };

      const submitUrl = `${API_BASE_URL}/audits/optin/${encodeURIComponent(auditId)}${accessKey ? `?access_key=${encodeURIComponent(accessKey)}` : ''}`;
      const response = await axios.post(submitUrl, payload, {
        headers: {
          Authorization: `Bearer ${API_KEY}`,
          'Content-Type': 'application/json'
        }
      });

      setSubmitSuccess(response.data || {});
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setSubmitError(null);
        setSubmitSuccess({ already_completed: true });
        setIsAlreadyCompleted(true);
      } else if (status === 403) {
        setSubmitError('Zugriff verweigert. Bitte prüfen Sie die Zugangsdaten.');
      } else if (status === 404) {
        setSubmitError('Opt-In oder Audit nicht gefunden.');
      } else if (status === 400) {
        setSubmitError('Bitte prüfen Sie die ausgewählten Checkboxen.');
      } else {
        const errorMessage = formatErrorMessage(err?.response?.data) || err?.message || 'Die Opt-In Übermittlung war nicht erfolgreich.';
        setSubmitError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div className="upload-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '40px' }}>
          <div style={{ color: '#6B7280' }}>Lade...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <div className="upload-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '40px', maxWidth: '720px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginBottom: '40px' }}>
              <img src={Logo} alt="Nebenkostenprofi Logo" style={{ height: '44px', width: 'auto' }} />
              <div style={{ fontSize: '1rem', color: '#111827', fontWeight: 600 }}>Nebenkostenprofi.de</div>
            </div>
            <div style={{ color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', padding: '12px 16px', borderRadius: '8px' }}>
              <strong>Fehler:</strong> {String(error)}
            </div>
          </div>
        </div>
        <div style={{ width: '100%', background: '#F3F4F6', borderTop: '1px solid #E5E7EB', padding: '16px 24px' }}>
          <div style={{ maxWidth: '1024px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <a href={AGB_URL} target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>AGB</a>
              <span style={{ color: '#9CA3AF' }}>|</span>
              <a href={PRIVACY_URL} target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>Datenschutzbestimmungen</a>
              <span style={{ color: '#9CA3AF' }}>|</span>
              <a href="https://www.nebenkostenprofi.de" target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>Startseite</a>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (submitSuccess || isAlreadyCompleted) {
    const isCompletedMessage = Boolean(submitSuccess?.already_completed || isAlreadyCompleted);
    return (
      <>
        <div
          className="App"
          style={{
            minHeight: '100svh',
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'visible',
            padding: 0,
            paddingBottom: 'env(safe-area-inset-bottom, 0px)'
          }}
        >
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div className="upload-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '40px', maxWidth: '720px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
                <img src={Logo} alt="Nebenkostenprofi Logo" style={{ height: '44px', width: 'auto' }} />
                <div style={{ fontSize: '1rem', color: '#111827', fontWeight: 600 }}>Nebenkostenprofi.de</div>
              </div>
              <h1 style={{ marginBottom: '12px' }}>Vielen Dank!</h1>
              <p style={{ color: '#4B5563', marginBottom: '16px' }}>
                {isCompletedMessage ? 'Ihr Opt-In wurde bereits erfasst.' : 'Ihr Opt-In wurde erfolgreich erfasst.'}
              </p>
              {submitSuccess?.audit_start?.error && (
                <div style={{ color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', padding: '12px 16px', borderRadius: '8px' }}>
                  <strong>Hinweis:</strong> {submitSuccess.audit_start.error}
                </div>
              )}
            </div>
          </div>
          <div style={{ width: '100%', background: '#F3F4F6', borderTop: '1px solid #E5E7EB', padding: '16px 24px' }}>
            <div style={{ maxWidth: '1024px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <a href={AGB_URL} target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>AGB</a>
                <span style={{ color: '#9CA3AF' }}>|</span>
                <a href={PRIVACY_URL} target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>Datenschutzbestimmungen</a>
                <span style={{ color: '#9CA3AF' }}>|</span>
                <a href="https://www.nebenkostenprofi.de" target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>Startseite</a>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className="App"
        style={{
          minHeight: '100svh',
          height: 'auto',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'visible',
          padding: 0,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
      >
        <div
          className="upload-container"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: '40px',
            maxWidth: '720px',
            width: '100%',
            flex: 'initial',
            minHeight: 'auto',
            margin: '0 auto'
          }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginBottom: '48px' }}>
            <img src={Logo} alt="Nebenkostenprofi Logo" style={{ height: '44px', width: 'auto' }} />
            <div style={{ fontSize: '1rem', color: '#111827', fontWeight: 600 }}>Nebenkostenprofi.de</div>
            <div style={{ marginTop: '8px', fontSize: '0.85rem', color: '#6B7280', letterSpacing: '0.04em' }}>
              Ihre Einwilligung
            </div>
            <div style={{ fontSize: '1.7rem', fontWeight: 800, color: '#111827', fontFamily: 'var(--font-heading)', lineHeight: 1.1 }}>
              {optinSpec?.title || 'Zustimmung'}
            </div>
          </div>

          <div style={{ width: '100%', maxWidth: '560px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {submitError && (
              <div style={{ color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', padding: '12px 16px', borderRadius: '8px' }}>
                <strong>Fehler:</strong> {submitError}
              </div>
            )}

            <div style={{ textAlign: 'left', color: '#374151', lineHeight: 1.6, display: 'flex', flexDirection: 'column' }}>
              <div
                ref={contentRef}
                onScroll={(event) => {
                  const target = event.currentTarget;
                  const isAtBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 2;
                  if (isAtBottom && !hasScrolledToBottom) {
                    setHasScrolledToBottom(true);
                    if (actionsRef.current) {
                      actionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }
                }}
                style={{
                  minHeight: '160px',
                  maxHeight: '45vh',
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  border: '1px solid #E5E7EB',
                  borderRadius: '18px',
                  padding: '14px 16px',
                  background: '#FFFFFF',
                  color: '#374151',
                  fontSize: '0.95rem',
                  lineHeight: 1.55,
                  boxShadow: '0 10px 24px rgba(17, 24, 39, 0.06)'
                }}
              >
                <ReactMarkdown>{optinBody}</ReactMarkdown>
              </div>
              {requiresScroll && !hasScrolledToBottom && (
                <div style={{ marginTop: '8px', fontSize: '0.9rem', color: '#6B7280' }}>
                  Bitte scrollen Sie bis zum Ende, um fortzufahren.
                </div>
              )}
              {optinSpec?.links && optinSpec.links.length > 0 && (
                <div style={{ marginTop: '12px', marginBottom: '4px', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '10px' }}>
                  {optinSpec.links.map((link) => (
                    <a
                      key={`${link.label}-${link.url}`}
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px',
                        padding: '8px 12px',
                        width: '100%',
                        maxWidth: '360px',
                        borderRadius: '999px',
                        border: '1px solid #E5E7EB',
                        background: '#F9FAFB',
                        color: '#111827',
                        textDecoration: 'none',
                        fontSize: '0.9rem',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease, border-color 0.15s ease',
                        boxShadow: '0 1px 2px rgba(16, 24, 40, 0.08)'
                      }}
                      onMouseEnter={(event) => {
                        event.currentTarget.style.transform = 'translateY(-1px)';
                        event.currentTarget.style.boxShadow = '0 6px 14px rgba(16, 24, 40, 0.12)';
                        event.currentTarget.style.background = '#FFFFFF';
                        event.currentTarget.style.borderColor = '#BBF7D0';
                      }}
                      onMouseLeave={(event) => {
                        event.currentTarget.style.transform = 'translateY(0)';
                        event.currentTarget.style.boxShadow = '0 1px 2px rgba(16, 24, 40, 0.08)';
                        event.currentTarget.style.background = '#F9FAFB';
                        event.currentTarget.style.borderColor = '#E5E7EB';
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'left' }}>
                        {link.label || link.url}
                      </span>
                      <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', color: '#4B5563' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M14 5h5v5M10 14l9-9M19 14v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div ref={actionsRef} style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {optinSpec?.checkboxes && optinSpec.checkboxes.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {optinSpec.checkboxes.map((checkbox) => (
                    <label
                      key={checkbox.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '12px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '16px',
                        background: '#FFFFFF',
                        boxShadow: '0 6px 16px rgba(17, 24, 39, 0.04)'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checkedBoxIds.includes(checkbox.id)}
                        onChange={() => handleCheckboxToggle(checkbox.id)}
                        style={{ marginTop: '3px' }}
                      />
                      <span style={{ color: '#111827' }}>
                        {checkbox.display_text}
                        {checkbox.optional === false && (
                          <span style={{ marginLeft: '6px', color: '#DC2626' }}>*</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={isSubmitting || (requiresScroll && !hasScrolledToBottom)}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  background: isSubmitting || (requiresScroll && !hasScrolledToBottom) ? '#9CA3AF' : 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '18px 32px',
                  borderRadius: '16px',
                  cursor: isSubmitting || (requiresScroll && !hasScrolledToBottom) ? 'not-allowed' : 'pointer',
                  fontSize: '18px',
                  fontWeight: 700,
                  transition: 'all 0.2s ease',
                  boxShadow: isSubmitting || (requiresScroll && !hasScrolledToBottom) ? 'none' : '0 14px 28px rgba(22, 163, 74, 0.22)',
                  outline: 'none'
                }}
                onMouseOver={(e) => {
                  if (!isSubmitting && !(requiresScroll && !hasScrolledToBottom)) {
                    e.target.style.background = 'linear-gradient(135deg, #15803d 0%, #166534 100%)';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 18px 32px rgba(22, 163, 74, 0.28)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isSubmitting && !(requiresScroll && !hasScrolledToBottom)) {
                    e.target.style.background = 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 14px 28px rgba(22, 163, 74, 0.22)';
                  }
                }}
              >
                {isSubmitting ? 'Wird übermittelt...' : 'Zustimmen'}
              </button>
            </div>
          </div>
        </div>
        <div style={{ width: '100%', background: '#F3F4F6', borderTop: '1px solid #E5E7EB', padding: '16px 24px' }}>
          <div style={{ maxWidth: '1024px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <a href={AGB_URL} target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>AGB</a>
              <span style={{ color: '#9CA3AF' }}>|</span>
              <a href={PRIVACY_URL} target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>Datenschutzbestimmungen</a>
              <span style={{ color: '#9CA3AF' }}>|</span>
              <a href="https://www.nebenkostenprofi.de" target="_blank" rel="noreferrer" style={{ color: '#111827', textDecoration: 'none' }}>Startseite</a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
